import React, { useEffect } from "react";
import {
  APP_SWITCH_DURATION,
  APP_SWITCH_FAN_STEP_ROTATE,
  APP_SWITCH_FAN_STEP_X,
  handleGetAppLogo,
} from "../../../utils/constants/GlobalAppAndLanguage";
import useAmsConfig from "../../../hooks/useAmsConfig";
import useZustandStore from "../../../zustandStore/useZustandStore";
import "../../../styles/appSwitchTransition.css";

/** Spread of a card that is not the new app: alternating left and right, further out for each pair. */
const handleFanStyle = ({ index }) => {
  const side = index % 2 === 0 ? -1 : 1;
  const step = Math.floor(index / 2) + 1;
  return {
    "--fan-x": `${side * step * APP_SWITCH_FAN_STEP_X}px`,
    "--fan-r": `${side * step * APP_SWITCH_FAN_STEP_ROTATE}deg`,
    "--rest-x": `${side * step * 22}px`,
    "--rest-r": `${side * step * 6}deg`,
    zIndex: 5 - step,
  };
};

/** App cards fan out and the new app's card comes to the front when the app or language changes. */
const AppSwitchTransition = () => {
  const appSwitch = useZustandStore((s) => s.appSwitch);
  const setAppSwitch = useZustandStore((s) => s.setAppSwitch);
  const { appNames, languageMapping } = useAmsConfig();

  useEffect(() => {
    if (!appSwitch) return undefined;
    const timer = setTimeout(() => setAppSwitch(null), APP_SWITCH_DURATION);
    return () => clearTimeout(timer);
  }, [appSwitch, setAppSwitch]);

  if (!appSwitch) return null;

  const { to } = appSwitch;
  const appLabel = appNames.find((a) => a.value === to.app)?.label || to.app;
  const others = appNames.filter((a) => a.value !== to.app);

  return (
    <div
      key={`${to.app}-${to.language}`}
      role="status"
      aria-live="polite"
      className="app-switch-backdrop fixed inset-0 z-[10000] flex flex-col items-center justify-center bg-[#f5f6fa]/90 backdrop-blur-sm"
    >
      <div className="relative h-[190px] w-[160px]">
        {others.map((a, index) => (
          <div
            key={a.value}
            className="app-switch-card absolute inset-0 flex items-center justify-center rounded-3xl bg-white shadow-[0_18px_40px_rgba(43,37,96,0.16)]"
            style={handleFanStyle({ index })}
          >
            <img
              src={handleGetAppLogo({ app: a.value })}
              alt=""
              className="h-20 w-20 rounded-2xl object-cover"
            />
          </div>
        ))}
        <div className="app-switch-card-target absolute inset-0 flex items-center justify-center rounded-3xl bg-white shadow-[0_24px_50px_rgba(43,37,96,0.22)] ring-2 ring-[#7f7ad5]/30">
          <img
            src={handleGetAppLogo({ app: to.app })}
            alt={appLabel}
            className="h-20 w-20 rounded-2xl object-cover"
          />
        </div>
      </div>
      <p className="app-switch-title mt-10 text-xl font-semibold text-[#2b2560]">
        {appLabel} · {languageMapping[to.language] || to.language}
      </p>
    </div>
  );
};

export default AppSwitchTransition;
